import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Robust WebSocket hook with heartbeat + zombie detection.
 *
 * Why the extra machinery:
 * - Kubernetes ingress / load balancers often close idle WS connections
 *   after ~60 seconds of no traffic. Without a heartbeat the socket goes
 *   "zombie" — still reports readyState=OPEN on the client but no data
 *   gets through. Onclose/onerror never fire. Dispatcher stops receiving
 *   real-time updates and has to manually refresh the tab.
 * - Solution: client pings every 25s, server replies with pong. If we
 *   don't see a pong within 10s of a ping, we force-close and reconnect.
 * - Also reconnects proactively when the tab regains focus (laptop sleep,
 *   OS-level network change, etc.).
 */
export function useWebSocket(onMessage) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const heartbeatInterval = useRef(null);
  const pongTimeout = useRef(null);
  const onMessageRef = useRef(onMessage);

  // Keep latest callback without re-creating `connect` on every render.
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  const clearTimers = useCallback(() => {
    if (heartbeatInterval.current) { clearInterval(heartbeatInterval.current); heartbeatInterval.current = null; }
    if (pongTimeout.current) { clearTimeout(pongTimeout.current); pongTimeout.current = null; }
    if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
  }, []);

  const connect = useCallback(() => {
    // Iter 98 — attach `?demo=1` when the user is in the training sandbox
    // so the backend routes this connection into the demo broadcast scope
    // and its events never bleed into real dispatcher consoles.
    let demoFlag = false;
    try {
      const u = JSON.parse(localStorage.getItem("dispatch_user") || "null");
      demoFlag = !!(u && u.demo_mode);
    } catch { /* ignore */ }
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://")
      + "/api/ws" + (demoFlag ? "?demo=1" : "");
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      // Start heartbeat: ping every 25s, expect pong within 10s.
      heartbeatInterval.current = setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return;
        try {
          ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
          // Arm zombie-detection timeout.
          if (pongTimeout.current) clearTimeout(pongTimeout.current);
          pongTimeout.current = setTimeout(() => {
            // No pong in 10s → socket is dead even if readyState says OPEN.
            try { ws.close(); } catch {}
          }, 10000);
        } catch {}
      }, 25000);
    };

    ws.onclose = () => {
      setConnected(false);
      clearTimers();
      // Reconnect after short backoff.
      reconnectTimer.current = setTimeout(connect, 2000);
    };
    ws.onerror = () => { try { ws.close(); } catch {} };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        // Pong → cancel zombie timeout, connection is healthy.
        if (data.type === "pong") {
          if (pongTimeout.current) { clearTimeout(pongTimeout.current); pongTimeout.current = null; }
          return;
        }
        onMessageRef.current && onMessageRef.current(data);
      } catch {}
    };
  }, [clearTimers]);

  useEffect(() => {
    connect();
    // Reconnect proactively when tab regains focus (laptop wake, etc.).
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          clearTimers();
          connect();
        }
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearTimers();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      if (wsRef.current) { try { wsRef.current.close(); } catch {} }
    };
  }, [connect, clearTimers]);

  return connected;
}
